import { Vec3 } from '../../engine/math/vec3';
import { BIOMES, BiomeId, SPOTS } from '../world/Layout';
import { ITEM_DEFS, ItemId } from '../items/ItemDefs';
import type { Game } from '../Game';
import { RouteBot } from './RouteBot';

/**
 * §8.8 debug helpers (enabled with ?debug=1, always reachable from the console via window.game.debug).
 * 1-5 teleport to biomes, Alt+number spawns items, G toggles god mode, Tab toggles free-fly.
 */
export class Debug {
  enabled: boolean;
  god = false;
  freeFly = false;
  private panel: HTMLDivElement | null = null;
  private readonly flyPos = new Vec3();
  private flyYaw = 0;
  private flyPitch = 0;
  readonly bot: RouteBot;
  private readonly itemOrder: ItemId[] = ['sword', 'lantern', 'plank', 'magnet', 'goldKey', 'whiteKey', 'blackKey', 'chalice', 'greyDot'];

  constructor(private readonly game: Game) {
    this.enabled = new URLSearchParams(location.search).get('debug') === '1';
    this.bot = new RouteBot(game);
    if (this.enabled) {
      this.panel = document.createElement('div');
      this.panel.className = 'debug';
      game.ui.appendChild(this.panel);
    }
  }

  teleport(x: number, z: number, y?: number): void {
    const g = this.game;
    const th = g.statics.terrainHeight(x, z);
    const p = new Vec3(x, y ?? g.statics.ground(x, th + 0.5, z, 1.0).y, z);
    g.player.teleport(p);
    g.tpc.snapTo(p);
  }
  teleportBiome(id: BiomeId): void {
    const spots: Record<BiomeId, Vec3> = {
      hearth: SPOTS.spawn, marsh: new Vec3(-150, 0, 0), dunes: new Vec3(0, 0, -150), ironwood: new Vec3(150, 0, 0), cistern: SPOTS.cisternEntry,
    };
    const s = spots[id];
    this.teleport(s.x, s.z);
  }
  spawnItem(id: ItemId): void {
    const g = this.game;
    const p = g.player.position.clone();
    p.x += Math.sin(g.player.facing) * 1.5; p.z += Math.cos(g.player.facing) * 1.5;
    const existing = g.world.items.get(id);
    if (existing) g.world.items.release(existing, p.clone().add(new Vec3(0, 1, 0)));
    else g.world.items.spawn(id, p);
  }

  /** §8.8: scripted agent that plays the Mode A critical path through the input layer. */
  runCriticalPath(): void { this.bot.runCriticalPath(); }

  /** Per sim step: hotkeys + panel. */
  update(dt: number): void {
    const g = this.game, input = g.input;
    this.bot.update(dt);
    if (!this.enabled) return;
    const biomes: BiomeId[] = ['hearth', 'marsh', 'dunes', 'ironwood', 'cistern'];
    for (let i = 0; i < 5; i++) if (input.wasPressed('Digit' + (i + 1)) && !input.isDown('AltLeft')) this.teleportBiome(biomes[i]);
    for (let i = 0; i < 9; i++) if (input.wasPressed('Digit' + (i + 1)) && input.isDown('AltLeft')) this.spawnItem(this.itemOrder[i]);
    if (input.wasPressed('KeyG')) { this.god = !this.god; }
    if (input.wasPressed('Tab')) {
      this.freeFly = !this.freeFly;
      if (this.freeFly) { this.flyPos.copy(g.camera.position); this.flyYaw = g.tpc.yaw; this.flyPitch = g.tpc.pitch; g.player.state = 'frozen'; }
      else { g.player.state = 'idle'; }
    }
    if (this.god) g.player.hearts = 3;
    if (this.freeFly) {
      const [dx, dy] = [0, 0];
      void dx; void dy;
      const [ax, ay] = input.moveAxis();
      const sp = input.isDown('ShiftLeft') ? 40 : 12;
      const f = new Vec3(Math.sin(this.flyYaw) * Math.cos(this.flyPitch), -Math.sin(this.flyPitch), Math.cos(this.flyYaw) * Math.cos(this.flyPitch));
      const r = new Vec3(-Math.cos(this.flyYaw), 0, Math.sin(this.flyYaw));
      this.flyPos.addScaled(f, ay * sp * dt).addScaled(r, ax * sp * dt);
      if (input.isDown('KeyQ')) this.flyPos.y -= sp * dt;
      if (input.isDown('KeyR')) this.flyPos.y += sp * dt;
    }
    if ((g.loop.frameCount & 7) === 0 && this.panel) {
      const p = g.player.position;
      const it = g.world.items.all.map((i) => `${i.def.id.padEnd(9)} ${i.loc.padEnd(6)} ${i.t.position.x.toFixed(0)},${i.t.position.z.toFixed(0)}`).join('\n');
      this.panel.textContent = `biome ${g.world.biome}  god ${this.god}  fly ${this.freeFly}\npos ${p.x.toFixed(1)} ${p.y.toFixed(1)} ${p.z.toFixed(1)}  hearts ${g.player.hearts}\nstate ${g.player.state}  held ${g.world.items.held?.def.id ?? '-'}\ndeaths ${g.world.deaths}  t ${(g.time - g.world.startTime).toFixed(0)}s\n\n${it}\n\n1-5 biome · Alt+1-9 item · G god · Tab fly`;
    }
  }

  /** Free-fly camera override, applied after the third-person camera. */
  applyCamera(dx: number, dy: number): void {
    if (!this.freeFly) return;
    const g = this.game;
    this.flyYaw -= dx * g.tpc.sensitivity;
    this.flyPitch = Math.max(-1.5, Math.min(1.5, this.flyPitch + dy * g.tpc.sensitivity));
    g.camera.position.copy(this.flyPos);
    g.camera.target.set(this.flyPos.x + Math.sin(this.flyYaw) * Math.cos(this.flyPitch), this.flyPos.y - Math.sin(this.flyPitch), this.flyPos.z + Math.cos(this.flyYaw) * Math.cos(this.flyPitch));
  }

  biomeCenter(id: BiomeId): Vec3 { return BIOMES[id].center; }
  itemNames(): ItemId[] { return Object.keys(ITEM_DEFS) as ItemId[]; }
}
